import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for auto-saving data at intervals and on field blur
 * @param {Object} data - The data to save
 * @param {Function} saveFunction - The function to call to save data
 * @param {number} intervalMs - Auto-save interval in milliseconds (default: 30000)
 * @param {boolean} enabled - Whether auto-save is enabled
 */
export function useAutoSave(data, saveFunction, intervalMs = 30000, enabled = true) {
  const lastSavedRef = useRef(JSON.stringify(data));
  const timerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Check if data has changed
  const hasChanges = useCallback(() => {
    return JSON.stringify(data) !== lastSavedRef.current;
  }, [data]);

  // Save function that updates lastSaved
  const save = useCallback(async () => {
    if (!enabled || !hasChanges() || !isMountedRef.current) return;

    try {
      await saveFunction(data);
      lastSavedRef.current = JSON.stringify(data);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [data, saveFunction, hasChanges, enabled]);

  // Save on blur handler
  const saveOnBlur = useCallback(() => {
    if (hasChanges()) {
      save();
    }
  }, [save, hasChanges]);

  // Set up interval auto-save
  useEffect(() => {
    if (!enabled) return;

    timerRef.current = setInterval(() => {
      if (hasChanges()) {
        save();
      }
    }, intervalMs);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [save, hasChanges, intervalMs, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Save on window unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges()) {
        // Try to save synchronously (won't always work)
        save();
        // Show browser's default "unsaved changes" dialog
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [save, hasChanges]);

  return {
    saveOnBlur,
    saveNow: save,
    hasChanges: hasChanges(),
  };
}

export default useAutoSave;
